import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { demoAPI, shouldUseDemoMode, validateDemoToken, DEMO_PROFILES } from '../utils/demoMode';
import { BACKEND_CONFIG, DEMO_MODE_CONFIG } from '../utils/productionConfig';
import { logFailedRequest } from '../components/DebugLogger';

// Centralized API URL configuration
// Priority: VITE_API_URL > VITE_BACKEND_URL > localStorage > localhost
const getBackendURL = () => {
  // Check for VITE_API_URL first (includes /api)
  if (typeof import.meta.env !== 'undefined' && import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Check for VITE_BACKEND_URL (base URL without /api)
  if (typeof import.meta.env !== 'undefined' && import.meta.env.VITE_BACKEND_URL) {
    const url = `${import.meta.env.VITE_BACKEND_URL}/api`;
    return url;
  }

  // Check localStorage for manually set backend URL (useful for Figma Make)
  const storedBackendURL = localStorage.getItem('BACKEND_URL');
  if (storedBackendURL) {
    const url = storedBackendURL.endsWith('/api') ? storedBackendURL : `${storedBackendURL}/api`;
    return url;
  }

  // Default to localhost
  // Silent fallback - suppress warning in production
  if (!sessionStorage.getItem('backendConfigLogged')) {
    sessionStorage.setItem('backendConfigLogged', 'true');
  }
  return BACKEND_CONFIG.apiUrl;
};

const API_URL = getBackendURL();

// Export BACKEND_URL and SOCKET_URL for components that need them
export const BACKEND_URL = BACKEND_CONFIG.url;
export const SOCKET_URL = BACKEND_CONFIG.socketUrl;

// Export for use in other components
export { API_URL, DEMO_MODE_CONFIG };

// Check if we're in demo mode
const isDemoMode = shouldUseDemoMode();

// Create and export a centralized axios instance with interceptors
export const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Request interceptor - Add auth token and update baseURL dynamically
axiosInstance.interceptors.request.use(
  (config) => {
    // Update baseURL dynamically from localStorage
    const storedBackendURL = localStorage.getItem('BACKEND_URL');
    if (storedBackendURL) {
      const url = storedBackendURL.endsWith('/api') ? storedBackendURL : `${storedBackendURL}/api`;
      config.baseURL = url;
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for auth errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Log failed request to debug logger
    const config = error.config;
    if (config) {
      logFailedRequest({
        method: (config.method || 'GET').toUpperCase(),
        url: config.url || config.baseURL || 'Unknown URL',
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorMessage: error.message || 'Network Error',
        requestData: config.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : undefined,
        responseData: error.response?.data,
        headers: config.headers
      });
    }

    // In demo mode, don't redirect on 401
    if (!isDemoMode && error.response?.status === 401) {
      // Token expired or invalid - only clear token, don't force page reload
      localStorage.removeItem('token');
      // Let the app handle navigation through normal flow
    }
    return Promise.reject(error);
  }
);

class APIClient {
  private client: AxiosInstance;
  private demoMode: boolean;

  constructor() {
    this.demoMode = isDemoMode;

    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      console.log(`🎭 API Client initialized in ${this.demoMode ? 'DEMO' : 'PRODUCTION'} mode`);
    }

    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });

    // Request interceptor - Add auth token and update baseURL dynamically
    this.client.interceptors.request.use(
      (config) => {
        // Update baseURL dynamically from localStorage
        const storedBackendURL = localStorage.getItem('BACKEND_URL');
        if (storedBackendURL) {
          const url = storedBackendURL.endsWith('/api') ? storedBackendURL : `${storedBackendURL}/api`;
          config.baseURL = url;
        }

        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for auth errors
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        // Log failed request to debug logger
        const config = error.config;
        if (config) {
          logFailedRequest({
            method: (config.method || 'GET').toUpperCase(),
            url: config.url || config.baseURL || 'Unknown URL',
            status: error.response?.status,
            statusText: error.response?.statusText,
            errorMessage: error.message || 'Network Error',
            requestData: config.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : undefined,
            responseData: error.response?.data,
            headers: config.headers
          });
        }

        // In demo mode, don't redirect on 401
        if (!this.demoMode && error.response?.status === 401) {
          // Token expired or invalid - only clear token, don't force page reload
          localStorage.removeItem('token');
          // Let the app handle navigation through normal flow
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async register(data: any) {
    if (this.demoMode) {
      return await demoAPI.register(data);
    }
    const response = await this.client.post('/auth/register', data);
    return response.data;
  }

  async login(email: string, password: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Logging in with', email);
      const result = await demoAPI.login(email, password);
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Invalid credentials');
      }
    }
    const response = await this.client.post('/auth/login', { email, password });
    return response.data;
  }

  async truecallerLogin(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Truecaller login');
      const result = await demoAPI.truecallerLogin(data);
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Truecaller login failed');
      }
    }
    const response = await this.client.post('/auth/truecaller-login', data);
    return response.data;
  }

  async sendOTP(phone?: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Simulating OTP send');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, message: 'OTP sent (demo mode)' };
    }
    const response = await this.client.post('/auth/send-otp', { phone });
    return response.data;
  }

  async verifyOTP(otp: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Simulating OTP verification');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, message: 'OTP verified (demo mode)' };
    }
    const response = await this.client.post('/auth/verify-otp', { otp });
    return response.data;
  }

  async getMe() {
    if (this.demoMode) {
      const token = localStorage.getItem('token');
      if (token) {
        const result = await demoAPI.loadUser(token);
        if (result.success) {
          return result.data;
        }
      }
      throw new Error('Not authenticated');
    }
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  async logout() {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Logging out');
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.post('/auth/logout');
    return response.data;
  }

  // Profile endpoints
  async getMatches(filters?: any) {
    if (this.demoMode) {
      const result = await demoAPI.getProfiles();
      return result.data;
    }
    const response = await this.client.get('/profiles/matches', { params: filters });
    return response.data;
  }

  async getProfile(profileId: string) {
    if (this.demoMode) {
      const result = await demoAPI.getProfile(profileId);
      if (result.success) {
        return result.data;
      }
      throw new Error(result.message || 'Profile not found');
    }
    const response = await this.client.get(`/profiles/${profileId}`);
    return response.data;
  }

  async getMyProfile() {
    if (this.demoMode) {
      const token = localStorage.getItem('token');
      if (token) {
        const user = validateDemoToken(token);
        if (user) {
          return { profile: user };
        }
      }
      throw new Error('Not authenticated');
    }
    const response = await this.client.get('/profiles/me');
    return response.data;
  }

  async updateProfile(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating profile', data);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, profile: data };
    }
    const response = await this.client.put('/profiles/me', data);
    return response.data;
  }

  async uploadPhoto(photoData: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Uploading photo');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400' };
    }
    const response = await this.client.post('/profiles/photos', photoData);
    return response.data;
  }

  async deletePhoto(photoId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting photo', photoId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.delete(`/profiles/photos/${photoId}`);
    return response.data;
  }

  async sendInterest(profileId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Sending interest to', profileId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, message: 'Interest sent' };
    }
    const response = await this.client.post(`/profiles/${profileId}/interest`);
    return response.data;
  }

  async respondToInterest(interestId: string, status: 'accepted' | 'rejected') {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Responding to interest', interestId, status);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.patch(`/profiles/interests/${interestId}`, { status });
    return response.data;
  }

  async shortlistProfile(profileId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Shortlisting profile', profileId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.post(`/profiles/${profileId}/shortlist`);
    return response.data;
  }

  async getShortlisted() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { profiles: DEMO_PROFILES.slice(0, 2) };
    }
    const response = await this.client.get('/profiles/shortlisted');
    return response.data;
  }

  // Chat endpoints
  async getChats() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { chats: [] };
    }
    const response = await this.client.get('/chats');
    return response.data;
  }

  async createChat(participantId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Creating chat with', participantId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        chat: {
          _id: `chat-${Date.now()}`,
          participants: [participantId]
        }
      };
    }
    const response = await this.client.post('/chats/create', { participantId });
    return response.data;
  }

  async getChatMessages(chatId: string) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { messages: [] };
    }
    const response = await this.client.get(`/chats/${chatId}/messages`);
    return response.data;
  }

  async sendMessage(chatId: string, content: string, messageType?: string, fileUrl?: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Sending message', content);
      const result = await demoAPI.sendMessage(chatId, content);
      return result.data;
    }
    const response = await this.client.post(`/chats/${chatId}/messages`, {
      content,
      messageType,
      fileUrl
    });
    return response.data;
  }

  async markChatAsRead(chatId: string) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 200));
      return { success: true };
    }
    const response = await this.client.patch(`/chats/${chatId}/read`);
    return response.data;
  }

  // Blog endpoints
  async getBlogs(filters?: any) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { blogs: [], total: 0 };
    }
    const response = await this.client.get('/blogs', { params: filters });
    return response.data;
  }

  async getBlog(slug: string) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      throw new Error('Blog not found');
    }
    const response = await this.client.get(`/blogs/${slug}`);
    return response.data;
  }

  async createBlog(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Creating blog', data);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, blog: { ...data, _id: `blog-${Date.now()}` } };
    }
    const response = await this.client.post('/blogs', data);
    return response.data;
  }

  async updateBlog(blogId: string, data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating blog', blogId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put(`/blogs/${blogId}`, data);
    return response.data;
  }

  async deleteBlog(blogId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting blog', blogId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.delete(`/blogs/${blogId}`);
    return response.data;
  }

  async submitGuestBlog(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Submitting guest blog');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/blogs/guest-submit', data);
    return response.data;
  }

  async approveBlog(blogId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Approving blog', blogId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.post(`/blogs/${blogId}/review`, { status: 'published' });
    return response.data;
  }

  async rejectBlog(blogId: string, reason: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Rejecting blog', blogId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.post(`/blogs/${blogId}/review`, { status: 'rejected', rejectionReason: reason });
    return response.data;
  }

  async likeBlog(blogId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Liking blog', blogId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.post(`/blogs/${blogId}/like`);
    return response.data;
  }

  async addComment(blogId: string, content: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Adding comment', content);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.post(`/blogs/${blogId}/comments`, { content });
    return response.data;
  }

  // Plan endpoints
  async getPlans() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { plans: [] };
    }
    const response = await this.client.get('/plans');
    return response.data;
  }

  async getPlan(planId: string) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      throw new Error('Plan not found');
    }
    const response = await this.client.get(`/plans/${planId}`);
    return response.data;
  }

  async createPlan(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Creating plan', data);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/plans', data);
    return response.data;
  }

  async updatePlan(planId: string, data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating plan', planId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put(`/plans/${planId}`, data);
    return response.data;
  }

  async deletePlan(planId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting plan', planId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.delete(`/plans/${planId}`);
    return response.data;
  }

  // Coupon endpoints
  async getCoupons(filters?: any) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { coupons: [] };
    }
    const response = await this.client.get('/coupons', { params: filters });
    return response.data;
  }

  async getMyCoupons() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { coupons: [] };
    }
    const response = await this.client.get('/coupons/my-coupons');
    return response.data;
  }

  async createCoupon(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Creating coupon', data);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/coupons', data);
    return response.data;
  }

  async updateCoupon(couponId: string, data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating coupon', couponId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put(`/coupons/${couponId}`, data);
    return response.data;
  }

  async validateCoupon(code: string, planId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Validating coupon', code);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { valid: false, message: 'Demo mode - coupons not available' };
    }
    const response = await this.client.post('/coupons/validate', { code, planId });
    return response.data;
  }

  async deleteCoupon(couponId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting coupon', couponId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.delete(`/coupons/${couponId}`);
    return response.data;
  }

  // Payment endpoints
  async createOrder(planId: string, couponCode?: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Creating order for plan', planId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        success: false,
        message: 'Demo mode - payments not available. Connect to real backend for payment functionality.'
      };
    }
    const response = await this.client.post('/payments/create-order', { planId, couponCode });
    return response.data;
  }

  async verifyPayment(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Verifying payment');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/payments/verify', data);
    return response.data;
  }

  async getMyOrders() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { orders: [] };
    }
    const response = await this.client.get('/payments/orders');
    return response.data;
  }

  async getOrderDetails(orderId: string) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      throw new Error('Order not found');
    }
    const response = await this.client.get(`/payments/orders/${orderId}`);
    return response.data;
  }

  // Marketing endpoints
  async getMarketingStats(filters?: any) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { stats: {} };
    }
    const response = await this.client.get('/marketing/stats', { params: filters });
    return response.data;
  }

  async getPayouts() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { payouts: [] };
    }
    const response = await this.client.get('/marketing/payouts');
    return response.data;
  }

  async requestPayout(amount: number, method: string, accountDetails: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Requesting payout', amount);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, message: 'Payout request submitted (demo mode)' };
    }
    const response = await this.client.post('/payout/request', {
      amount,
      paymentMethod: method,
      accountDetails
    });
    return response.data;
  }

  async requestReactivation() {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Requesting reactivation');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/marketing/request-reactivation');
    return response.data;
  }

  async updateAccountDetails(accountDetails: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating account details');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/marketing/account-details', accountDetails);
    return response.data;
  }

  // Admin endpoints
  async getUsers(filters?: any) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { users: [], total: 0 };
    }
    const response = await this.client.get('/admin/users', { params: filters });
    return response.data;
  }

  async getUserDetails(userId: string) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      throw new Error('User not found');
    }
    const response = await this.client.get(`/admin/users/${userId}`);
    return response.data;
  }

  async updateUserStatus(userId: string, status: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating user status', userId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.patch(`/admin/users/${userId}/status`, status);
    return response.data;
  }

  async blockUser(userId: string, reason: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Blocking user', userId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post(`/admin/users/${userId}/block`, { reason });
    return response.data;
  }

  async unblockUser(userId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Unblocking user', userId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post(`/admin/users/${userId}/unblock`);
    return response.data;
  }

  async getPayoutRequests(filters?: any) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { requests: [] };
    }
    const response = await this.client.get('/admin/payouts', { params: filters });
    return response.data;
  }

  async approvePayout(requestId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Approving payout', requestId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post(`/admin/payouts/${requestId}/approve`);
    return response.data;
  }

  async rejectPayout(requestId: string, reason: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Rejecting payout', requestId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post(`/admin/payouts/${requestId}/reject`, { reason });
    return response.data;
  }

  async getReactivationRequests() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { requests: [] };
    }
    const response = await this.client.get('/admin/reactivation-requests');
    return response.data;
  }

  async approveReactivation(userId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Approving reactivation', userId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post(`/admin/users/${userId}/reactivate`);
    return response.data;
  }

  async rejectReactivation(userId: string, reason: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Rejecting reactivation', userId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post(`/admin/users/${userId}/reject-reactivation`, { reason });
    return response.data;
  }

  async getDashboardStats() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        stats: {
          totalUsers: 1250,
          activeUsers: 890,
          premiumUsers: 320,
          totalRevenue: 450000,
          monthlyRevenue: 125000
        }
      };
    }
    const response = await this.client.get('/admin/dashboard-stats');
    return response.data;
  }

  async getPendingBlogs() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { blogs: [] };
    }
    const response = await this.client.get('/admin/blogs/pending');
    return response.data;
  }

  async getReportedUsers(filters?: any) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          reportedUsers: [],
          pagination: {
            page: 1,
            limit: 20,
            total: 0,
            pages: 0
          }
        }
      };
    }
    const response = await this.client.get('/admin/reported-users', { params: filters });
    return response.data;
  }

  async updateReportStatus(reportId: string, data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating report status', reportId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.patch(`/admin/reports/${reportId}`, data);
    return response.data;
  }

  // Admin Settings endpoints
  async getDiscoverySettings() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          skipDuration: 24,
          interestDuration: 720,
          superInterestDuration: 1440,
          shortlistDuration: 2160,
          priorityWeights: {
            sameCity: 10,
            nearbyCities: 8,
            sameState: 6,
            age: 7,
            hobbies: 5,
            maritalStatus: 4,
            education: 3,
            religion: 3
          }
        }
      };
    }
    const response = await this.client.get('/admin/settings/discovery');
    return response.data;
  }

  async updateDiscoverySettings(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating discovery settings');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/admin/settings/discovery', data);
    return response.data;
  }

  async getProfileBoosterSettings() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          boostDuration: 24,
          superBoostDuration: 72,
          featuredBoostDuration: 168,
          boostMultiplier: 3,
          superBoostMultiplier: 5,
          featuredMultiplier: 10,
          minCreditsRequired: 10
        }
      };
    }
    const response = await this.client.get('/admin/profile-booster-settings');
    return response.data;
  }

  async updateProfileBoosterSettings(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating profile booster settings');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/admin/profile-booster-settings', data);
    return response.data;
  }

  // User endpoints
  async updateUser(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating user', data);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/users/me', data);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Changing password');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/users/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }

  async updatePreferences(preferences: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating preferences');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/users/preferences', preferences);
    return response.data;
  }

  async deleteAccount() {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting account');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.delete('/users/me');
    return response.data;
  }

  // Settings endpoints
  async getSettings() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        settings: {
          _id: 'demo-settings-001',
          branding: {
            name: 'SoulMate',
            logo: '',
            description: "India's most trusted matrimony service helping millions find their perfect life partner.",
            tagline: 'Find Your Perfect Match'
          },
          contactInfo: {
            address: '123 Matrimony Street, Mumbai, Maharashtra 400001',
            phone: '+91 1800-123-4567',
            email: 'support@soulmate.com'
          },
          features: {
            discoverTabEnabled: true,
            liveTabEnabled: false,
            activityTabEnabled: true,
            messagesTabEnabled: true,
            plansTabEnabled: true,
            addonPlansEnabled: false,
            smsInviteEnabled: true,
            genderBasedDiscovery: true
          },
          appBanner: {
            enabled: true,
            text: '📱 Download our official Matrimonial App for a faster & better experience!',
            buttonText: 'Download App',
            playStoreUrl: 'https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp',
            badgeText: 'GET OUR APP',
          },
          quickLinks: [
            { _id: 'ql1', title: 'About Us', url: '/about', order: 1, isActive: true },
            { _id: 'ql2', title: 'Success Stories', url: '/success-stories', order: 2, isActive: true }
          ],
          supportLinks: [
            { _id: 'sl1', title: 'Help Center', url: '/help', order: 1, isActive: true },
            { _id: 'sl2', title: 'Safety Tips', url: '/safety', order: 2, isActive: true }
          ],
          socialLinks: [
            { _id: 'so1', platform: 'facebook' as const, url: 'https://facebook.com', isActive: true },
            { _id: 'so2', platform: 'twitter' as const, url: 'https://twitter.com', isActive: true },
            { _id: 'so3', platform: 'instagram' as const, url: 'https://instagram.com', isActive: true },
            { _id: 'so4', platform: 'linkedin' as const, url: 'https://linkedin.com', isActive: true }
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      };
    }
    const response = await this.client.get('/settings/app');
    return response.data;
  }

  async updateBranding(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating branding');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/settings/branding', data);
    return response.data;
  }

  async updateContactInfo(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating contact info');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/settings/contact', data);
    return response.data;
  }

  async addQuickLink(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Adding quick link');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/settings/quick-links', data);
    return response.data;
  }

  async updateQuickLink(linkId: string, data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating quick link', linkId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put(`/settings/quick-links/${linkId}`, data);
    return response.data;
  }

  async deleteQuickLink(linkId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting quick link', linkId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.delete(`/settings/quick-links/${linkId}`);
    return response.data;
  }

  async addSupportLink(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Adding support link');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/settings/support-links', data);
    return response.data;
  }

  async updateSupportLink(linkId: string, data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating support link', linkId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put(`/settings/support-links/${linkId}`, data);
    return response.data;
  }

  async deleteSupportLink(linkId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting support link', linkId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.delete(`/settings/support-links/${linkId}`);
    return response.data;
  }

  async addSocialLink(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Adding social link');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/settings/social-links', data);
    return response.data;
  }

  async updateSocialLink(linkId: string, data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating social link', linkId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put(`/settings/social-links/${linkId}`, data);
    return response.data;
  }

  async deleteSocialLink(linkId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting social link', linkId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.delete(`/settings/social-links/${linkId}`);
    return response.data;
  }

  // 🔥 NEW: Feature settings
  async updateFeatureSettings(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating feature settings', data);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, features: data };
    }
    const response = await this.client.put('/settings/features', data);
    return response.data;
  }

  // 🔥 NEW: Auto Interest settings
  async updateAutoInterestSettings(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating auto-interest settings', data);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, autoInterestSettings: data };
    }
    const response = await this.client.put('/settings/auto-interest', data);
    return response.data;
  }

  // 🔥 NEW: App Banner settings
  async updateAppBannerSettings(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating app banner settings', data);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, appBanner: data };
    }
    const response = await this.client.put('/settings/app-banner', data);
    return response.data;
  }

  // FAQ endpoints
  async searchProfiles(filters: any) {
    if (this.demoMode) {
      const result = await demoAPI.getProfiles();
      return result.data;
    }
    const response = await this.client.post('/profiles/search', filters);
    return response.data;
  }

  async getFAQs(category?: string, search?: string) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { faqs: [] };
    }
    const params: any = {};
    if (category) params.category = category;
    if (search) params.search = search;

    const response = await this.client.get('/faqs', { params });
    return response.data;
  }

  async getAllFAQsAdmin() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { faqs: [] };
    }
    const response = await this.client.get('/faqs/admin');
    return response.data;
  }

  async createFAQ(data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Creating FAQ');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.post('/faqs/admin', data);
    return response.data;
  }

  async updateFAQ(faqId: string, data: any) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Updating FAQ', faqId);
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put(`/faqs/admin/${faqId}`, data);
    return response.data;
  }

  async deleteFAQ(faqId: string) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Deleting FAQ', faqId);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }
    const response = await this.client.delete(`/faqs/admin/${faqId}`);
    return response.data;
  }

  async reorderFAQs(faqs: Array<{ id: string; order: number }>) {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Reordering FAQs');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    const response = await this.client.put('/faqs/admin/reorder', { faqs });
    return response.data;
  }

  // Upload file helper
  async uploadFile(file: File, type: 'profile' | 'blog' | 'chat' = 'profile') {
    if (this.demoMode) {
      console.log('🎭 Demo Mode: Uploading file', file.name);
      await new Promise(resolve => setTimeout(resolve, 1000));
      return {
        success: true,
        url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400'
      };
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await this.client.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // Referral endpoints
  async getReferralPublicInfo() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          settings: {
            referralAmount: 500,
            referralType: 'fixed',
            referralPercentage: 10,
            minimumPayoutAmount: 500,
            welcomeMessage: 'Refer friends and earn rewards!',
            milestonesEnabled: true,
            milestones: [
              { referrals: 5, reward: 500, title: '₹500 bonus', description: 'Complete 5 referrals' },
              { referrals: 10, reward: 1500, title: '₹1,500 bonus + 1 Month Free', description: 'Complete 10 referrals' },
              { referrals: 25, reward: 5000, title: '₹5,000 bonus + 3 Months Free', description: 'Complete 25 referrals' },
              { referrals: 50, reward: 15000, title: '₹15,000 bonus + 6 Months Free', description: 'Complete 50 referrals' },
              { referrals: 100, reward: 50000, title: '₹50,000 bonus + Lifetime Premium', description: 'Complete 100 referrals' }
            ]
          },
          rewards: [
            { title: 'Friend buys Gold plan', description: 'Premium Gold subscription', rewardAmount: 1000, icon: 'crown', color: 'yellow' },
            { title: 'Friend buys Diamond plan', description: 'Premium Diamond subscription', rewardAmount: 2000, icon: 'gem', color: 'purple' },
            { title: 'Friend buys Platinum plan', description: 'Premium Platinum subscription', rewardAmount: 3000, icon: 'star', color: 'indigo' }
          ]
        }
      };
    }
    const response = await this.client.get('/referral/public-info');
    return response.data;
  }

  async getReferralSettings() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          settings: {
            referralAmount: 500,
            referralType: 'fixed',
            minimumPayoutAmount: 500,
            welcomeMessage: 'Refer friends and earn rewards!'
          }
        }
      };
    }
    const response = await this.client.get('/referral/settings');
    return response.data;
  }

  async getMyReferrals() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          referralCode: 'SOUL' + Math.random().toString(36).substring(2, 8).toUpperCase(),
          referrals: [],
          earnings: [],
          totalReferrals: 0,
          totalEarnings: 0,
          pendingEarnings: 0
        }
      };
    }
    const response = await this.client.get('/referral/my-referrals');
    return response.data;
  }

  async getReferralStatistics() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          totalReferrals: 0,
          successfulReferrals: 0,
          totalEarnings: 0,
          pendingEarnings: 0,
          approvedEarnings: 0
        }
      };
    }
    const response = await this.client.get('/referral/statistics');
    return response.data;
  }

  async getWalletBalance() {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          balance: 0,
          pendingBalance: 0,
          totalEarnings: 0
        }
      };
    }
    const response = await this.client.get('/wallet/balance');
    return response.data;
  }

  async getWalletTransactions(filters?: any) {
    if (this.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        data: {
          transactions: [],
          pagination: {
            page: 1,
            limit: 20,
            total: 0,
            pages: 0
          }
        }
      };
    }
    const response = await this.client.get('/wallet/transactions', { params: filters });
    return response.data;
  }

  // Generic HTTP methods for direct API calls
  async get(url: string, config?: any) {
    return await this.client.get(url, config);
  }

  async post(url: string, data?: any, config?: any) {
    return await this.client.post(url, data, config);
  }

  async put(url: string, data?: any, config?: any) {
    return await this.client.put(url, data, config);
  }

  async patch(url: string, data?: any, config?: any) {
    return await this.client.patch(url, data, config);
  }

  async delete(url: string, config?: any) {
    return await this.client.delete(url, config);
  }
}

export const apiClient = new APIClient();
export default apiClient;